import React, { memo } from 'react';
import { areEqual } from 'Utils/equalityChecks';

import { Table, TableBody, TableColumn, TableHeader, TableRow, Th } from 'Components/Table';
import { Modal } from 'Components/Modal';
import { DarkPurpleButton, DeleteButton } from 'Components/Button';
import { Icon } from 'Components/Icons';
import { formatCurrency, formatDate } from 'Utils/helpers';

import classes from './paymentList.module.css';

interface Props {
  payments?: any;
  sortBy?: string;
  onClickSort?: (sort: string) => void;
  cancellingPaymentId?: number | null;
  onCancelPaymentClick?: (paymentId: number) => void;
  onConfirmCancelPayment?: () => void;
  onDismissCancelModal?: () => void;
}

function PaymentList({
  payments,
  sortBy,
  onClickSort,
  cancellingPaymentId,
  onCancelPaymentClick,
  onConfirmCancelPayment,
  onDismissCancelModal,
}: Props) {
  const hasPayments = payments?.data?.length > 0;

  return (
    <>
      <Modal
        id="cancelPaymentModal"
        title="Cancel Payment Request"
        isOpen={!!cancellingPaymentId}
        dataBsBackdrop="static"
        dataBsKeyboard="false"
        modalHeader
        modalCloseClick={onDismissCancelModal}
      >
        <div className={classes.cancelModalMessage}>
          Are you sure you want to cancel this payment request? The payment link will stop working immediately.
        </div>
        <div className={classes.cancelModalButtons}>
          <DarkPurpleButton onClick={onDismissCancelModal}>Keep</DarkPurpleButton>
          <DeleteButton onClick={onConfirmCancelPayment}>Yes, Cancel Request</DeleteButton>
        </div>
      </Modal>

      <Table className={`table ${classes.paymentListWrapper}`}>
        <TableHeader>
          <TableRow>
            <Th>
              <span
                className={classes.thSpan}
                role="button"
                tabIndex={-1}
                onClick={() => onClickSort('-created_at')}
                onKeyUp={() => onClickSort('-created_at')}
              >
                Date
                {sortBy === '-created_at' ? (
                  <Icon className={classes.sortIcon} type="caretup" />
                ) : (
                  <Icon className={classes.sortIcon} type="caretdown" />
                )}
              </span>
            </Th>
            <Th>
              <span
                className={classes.thSpan}
                role="button"
                tabIndex={-1}
                onClick={() => onClickSort('-email')}
                onKeyUp={() => onClickSort('-email')}
              >
                Email
                {sortBy === '-email' ? (
                  <Icon className={classes.sortIcon} type="caretup" />
                ) : (
                  <Icon className={classes.sortIcon} type="caretdown" />
                )}
              </span>
            </Th>
            <Th>
              <span
                className={classes.thSpan}
                role="button"
                tabIndex={-1}
                onClick={() => onClickSort('-amount')}
                onKeyUp={() => onClickSort('-amount')}
              >
                Amount
                {sortBy === '-amount' ? (
                  <Icon className={classes.sortIcon} type="caretup" />
                ) : (
                  <Icon className={classes.sortIcon} type="caretdown" />
                )}
              </span>
            </Th>
            <Th>
              <span
                className={classes.thSpan}
                role="button"
                tabIndex={-1}
                onClick={() => onClickSort('-description')}
                onKeyUp={() => onClickSort('-description')}
              >
                Description
                {sortBy === '-description' ? (
                  <Icon className={classes.sortIcon} type="caretup" />
                ) : (
                  <Icon className={classes.sortIcon} type="caretdown" />
                )}
              </span>
            </Th>
            <Th />
          </TableRow>
        </TableHeader>
        {hasPayments && (
          <TableBody>
            {payments.data.map((payment) => (
              <TableRow key={payment.id}>
                <TableColumn>
                  <div>{formatDate(payment.updated_at, 'MMM d, yyyy h:mma')}</div>
                </TableColumn>
                <TableColumn>
                  <div>{payment.email}</div>
                </TableColumn>
                <TableColumn className={classes.columnContent}>
                  <div>
                    {formatCurrency(payment.amount / 100, payment.currency)} {payment.currency.toUpperCase()}
                  </div>
                </TableColumn>
                <TableColumn className={classes.columnContent}>
                  <div>{payment.description}</div>
                </TableColumn>
                <TableColumn>
                  <DeleteButton className={classes.cancelButton} onClick={() => onCancelPaymentClick?.(payment.id)}>
                    Cancel
                  </DeleteButton>
                </TableColumn>
              </TableRow>
            ))}
          </TableBody>
        )}
      </Table>
      {!hasPayments && (
        <div
          className={`d-flex justify-content-center align-items-center flex-column w-100 ${classes.noPaymentContent}`}
        >
          <p className={classes.noPaymentText}>No payment requests made yet.</p>
          <Icon type="rocketemblemtwo" />
        </div>
      )}
    </>
  );
}

PaymentList.defaultProps = {
  payments: [],
  sortBy: null,
  onClickSort: null,
  cancellingPaymentId: null,
  onCancelPaymentClick: null,
  onConfirmCancelPayment: null,
  onDismissCancelModal: null,
};

const PaymentListMemo = memo(PaymentList, areEqual);

export { PaymentListMemo as PaymentList };
